import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, PlusCircle } from "lucide-react";
import { useLang } from "../lib/i18n";

const LINKS = [
  { to: "/", label: "Home", key: "nav.home" },
  { to: "/category/businesses", label: "Businesses", key: "nav.businesses" },
  { to: "/category/services", label: "Services", key: "nav.services" },
  { to: "/category/jobs", label: "Jobs", key: "nav.jobs" },
  { to: "/category/events", label: "Events", key: "nav.events" },
  { to: "/category/community", label: "Community", key: "nav.community" },
  { to: "/about", label: "About", key: "nav.about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t, lang, setLang } = useLang();
  return (
    <header className="sticky top-0 z-50 bg-navy/95 backdrop-blur-md border-b border-[#2A4FA0]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo-link">
            <img src="/bh-icon-180.png" alt="BurmeseHub logo" className="w-10 h-10 rounded-full group-hover:scale-105 transition-transform duration-300" />
            <span className="leading-none">
              <span className="font-serif font-bold tracking-tight text-xl text-white">
                Burmese<span className="text-gold">Hub</span>
              </span>
              <span className="hidden lg:block text-[9px] font-mono uppercase tracking-[0.3em] text-white/50 mt-0.5">
                People · Businesses · Community
              </span>
            </span>
          </Link>
          <nav className={`hidden lg:flex items-center ${lang === "my" ? "gap-4" : "gap-7"}`} data-testid="desktop-nav">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `${lang === "my" ? "text-[13px]" : "text-sm"} whitespace-nowrap font-medium tracking-wide transition-colors duration-200 ${
                    isActive ? "text-gold" : "text-white/75 hover:text-white"
                  }`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full border border-[#2A4FA0] bg-navy-royal/60 p-0.5" data-testid="lang-toggle">
              <button
                onClick={() => setLang("en")}
                data-testid="lang-toggle-en"
                className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "en" ? "bg-gold text-navy" : "text-white/50 hover:text-white"}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("my")}
                data-testid="lang-toggle-my"
                style={{ fontFamily: "'Noto Sans Myanmar', sans-serif" }}
                className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "my" ? "bg-gold text-navy" : "text-white/50 hover:text-white"}`}
              >
                မြန်မာ
              </button>
            </div>
            <button
              onClick={() => navigate("/submit")}
              data-testid="list-your-business-btn"
              className="hidden sm:inline-flex items-center gap-1.5 bg-gold hover:bg-gold-dark text-navy text-xs font-bold px-4 py-2.5 rounded-lg transition-colors duration-200"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              {t("cta.list")}
            </button>
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#2A4FA0] bg-navy px-4 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className="block py-2.5 px-3 rounded-lg text-white/80 font-medium hover:bg-navy-royal hover:text-white"
            >
              {t(l.key)}
            </Link>
          ))}
          <Link
            to="/submit"
            onClick={() => setOpen(false)}
            data-testid="mobile-list-your-business-btn"
            className="block mt-2 text-center bg-gold text-navy font-bold px-5 py-3 rounded-lg"
          >
            {t("cta.list")}
          </Link>
        </div>
      )}
    </header>
  );
}
